import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

/**
 * About page component for Meyer Management ApS.
 * Displays information about Pia Meyer, her experience, and focus areas.
 */
const About = () => {
  return (
    <div className="bg-white">
      <Helmet>
        <title>Om mig - Meyer Management ApS</title>
        <meta
          name="description"
          content="Jeg hjælper ledere med at skabe økonomifunktioner, der fungerer – både fagligt og menneskeligt."
        />
        <html lang="da-DK" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-16 pb-16 bg-[#f7f4ed]">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <h1 className="text-5xl lg:text-6xl font-serif text-stone-900 mb-8">Om mig</h1>
            <p className="text-xl text-stone-600 leading-relaxed">
              Jeg hjælper ledere med at skabe økonomifunktioner, der fungerer – både fagligt og menneskeligt.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who Am I Section - Full Bleed Image Style */}
      <section className="bg-white overflow-hidden">
        <div className="flex flex-col lg:flex-row items-stretch">
          <div className="w-full lg:w-1/2 flex items-center">
            <div className="py-16 lg:py-24 px-6 sm:px-12 lg:pl-24 xl:pl-32 2xl:pl-48 max-w-4xl">
              <span className="font-serif text-3xl md:text-4xl text-stone-900 block mb-8">Hvem er jeg?</span>
              
              <div className="text-stone-600 text-lg leading-relaxed space-y-6">
                <p>Jeg hedder Pia Meyer, og jeg hjælper ledere med at skabe økonomifunktioner, der fungerer – både fagligt og menneskeligt.</p>
                <p>Jeg har arbejdet som økonomileder og konsulent i snart 20 år og trives i krydsfeltet mellem forretning, mennesker og udvikling.</p>
                <p>Jeg er drevet af at skabe arbejdsglæde, effektivitet og retning, og jeg tror på, at trivsel og gode resultater hænger uløseligt sammen.</p>
                <p>Som person er jeg smilende, empatisk og nysgerrig. Jeg tror på, at medarbejdernes trivsel er fundamentet for både kvalitet og produktivitet.</p>
                <p>Min passion er at forstå mennesker dybt og skabe en positiv indvirkning – at spille dem bedre og skabe trivsel i hver eneste interaktion, både personligt og professionelt.</p>
              </div>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="w-full lg:w-1/2 relative min-h-[500px] lg:min-h-[800px]"
          >
            <img
              src="https://assets.auxodev.dk/assets/b8a7f302-f5bc-457e-a48c-e9d29dfda424/8db0f501-901d-4d2c-b520-508e9da7fa60.jpg"
              alt="Pia Meyer"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="py-24 bg-[#f7f4ed]">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 mb-16">
              <div>
                <span className="font-serif text-3xl md:text-4xl text-stone-900 block mb-8">Erfaring og fokus</span>
                
                <div className="text-stone-600 text-lg leading-relaxed space-y-6">
                  <p>Gennem mere end 25 år i revisions- og konsulentbranchen har jeg erfaret, at mange virksomheder står over for de samme udfordringer, når forandringer og digitalisering rammer økonomifunktionen.</p>
                  <p>Derfor tror jeg på, at støtte, sparring og tydelig ledelse er nøglen til at få mennesker og systemer til at lykkes sammen.</p>
                  <p>Jeg har en skarp sans for struktur, systemer og sammenhæng, og jeg trives, når jeg kan gøre komplekse processer enkle og overskuelige.</p>
                  <p>Jeg har erfaring fra både hands-on økonomi, rekruttering og ledelse af økonomiteams – og har bidraget til sund vækst i både nye og etablerede afdelinger.</p>
                </div>
              </div>

              <div>
                <span className="font-serif text-2xl text-stone-900 block mb-6">Mine primære fokusområder:</span>
                <ul className="grid gap-6">
                  {[
                    "Ledelse af økonomiteams – både fagligt og menneskeligt",
                    "Interim og projektledelse i både SMV’er og større organisationer",
                    "Sparring for CFO’er og økonomiledere i forandring",
                    "Workshops og oplæg om trivsel, struktur og nærværende ledelse"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-4 bg-white p-6 shadow-sm border border-stone-100">
                      <div className="w-2 h-2 rounded-full bg-[#204f3d] mt-2.5 shrink-0" />
                      <span className="text-stone-700 font-medium">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="pt-12 border-t border-stone-200 text-center">
              <p className="text-stone-900 text-xl font-medium mb-8 max-w-3xl mx-auto">Jeg tror på, at stærke økonomifunktioner starter med mennesker – for mennesker udvikler mennesker. Når mennesker trives, kommer resultaterne af sig selv.</p>
              <Link
                to="/kontakt"
                className="inline-block px-8 py-4 rounded-full text-white bg-[#204f3d] font-medium text-center hover:opacity-90 transition-all shadow-lg shadow-stone-200"
              >
                Book en uforpligtende samtale
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
